import {
  popPageAction,
  popPageStartAction,
  pushPageAction,
  replacePageAction,
} from "../actions/router";
import { IStoreAction } from "../interfaces/all-store-actions";
import {
  IActionHandler,
  ICtaV2,
  IStoreCtaV2,
} from "../interfaces/event-actions";
import { IStatus } from "../interfaces/status";
import { useStatus } from "../hooks/status";
import { getStateValues } from "../util/state-values";
import { useQueryParameter } from "./session";

export const useActionEmitter = (
  actionHandlers: IActionHandler[],
  state: any,
  injectState?: boolean
): [IStatus, typeof emitCta, typeof onAction] => {
  const platform = useQueryParameter("platform");
  const [status, setStatus] = useStatus();

  const onAction: IActionHandler = (action, details) => {
    if (injectState) {
      if (details && details.state && details.state !== state) {
        details.state = { ...details.state, ...state };
      } else if (details) {
        details.state = state;
      } else {
        details = { state, analytics: null }
      }
    }
    for (let i = 0; i < actionHandlers.length; i++) {
      actionHandlers[i](action, details);
    }
  };

  const emitCta = (cta: ICtaV2, data?: { state: any }) => {
    setStatus({ state: "LOADING" });

    /* combine state */
    let ctaState = {};
    if (state) {
      ctaState = state;
    }
    if (data && data.state) {
      ctaState = { ...ctaState, ...data.state };
    }

    /* transform to store v2 */
    let storeCtaV2: IStoreCtaV2 = null;
    if (cta.target !== "STORE_V2") {
      storeCtaV2 = transformToStoreCtaV2(cta);
    } else {
      storeCtaV2 = cta;
    }

    /* get right action for the platform */
    const triggeringAction = getPlatformAction(storeCtaV2, platform);

    /* if action is an array */
    if (Array.isArray(triggeringAction)) {
      let index = 0;
      const sendNextAction = () => {
        const action = triggeringAction[index];
        index = index + 1;
        if (action)
          onAction(resolveActionPayload(ctaState, action, storeCtaV2), {
            state: ctaState,
            onComplete: sendNextAction,
            analytics: storeCtaV2.analytics,
          });
        else {
          console.log("action completed");
          setStatus({ state: "LOAD_SUCCESS" });
        }
      };
      sendNextAction();
    } else {
      onAction(resolveActionPayload(ctaState, triggeringAction, storeCtaV2), {
        state: ctaState,
        onComplete: () => {
          console.log("action completed");
          setStatus({ state: "LOAD_SUCCESS" });
        },
        analytics: storeCtaV2.analytics,
      });
    }
  };

  return [status, emitCta, onAction];
};

const transformToStoreCtaV2 = (cta: ICtaV2): IStoreCtaV2 => {
  if (cta.target === "STORE") {
    try {
      if (
        cta.action.store === "ROUTER" &&
        cta.action.type === "ROUTER_STORE PUSH PAGE"
      ) {
        if (
          cta.action.payload["props"] &&
          cta.action.payload["props"]["id"] === "REWARD_WRAPPER"
        ) {
          return {
            target: "STORE_V2",
            name: cta.name,
            action: pushPageAction({
              layout: {
                type_id: "FULL_PAGE",
                ui: {
                  content: {
                    animation: true,
                  },
                  css: {
                    container: {},
                  },
                },
              },
              content: {
                type_id: "REWARD_WRAPPER",
                fragment: {
                  rootId: null,
                },
                filter: {
                  campaignId: {
                    [cta.action.payload["props"]["data"]["campaignId"]]: true,
                  },
                },
              },
            }),
            platform: null,
            analytics: {
              eventName: "BUTTON_CLICKED",
              type: "TRACK",
            },
          };
        } else if (
          cta.action.payload?.content &&
          cta.action.payload?.content?.type_id === "PROGRAM_WRAPPER"
        ) {
          return {
            target: "STORE_V2",
            name: cta.name,
            action: pushPageAction({
              layout: cta.action.payload.layout,
              content: cta.action.payload.content,
            }),
            platform: null,
            analytics: {
              eventName: "BUTTON_CLICKED",
              type: "TRACK",
            },
          };
        } else if (
          cta.action.payload["props"] &&
          cta.action.payload["props"]["id"] === "WALLET_WRAPPER"
        ) {
          return {
            target: "STORE_V2",
            name: cta.name,
            action: pushPageAction({
              layout: {
                type_id: "FULL_PAGE",
                ui: {
                  content: {
                    animation: true,
                  },
                  css: {
                    container: {},
                  },
                },
              },
              content: {
                type_id: "WALLET_WRAPPER",
                fragment: {
                  rootId: null,
                },
                filter: {
                },
              },
            }),
            platform: null,
            analytics: {
              eventName: "BUTTON_CLICKED",
              type: "TRACK",
            },
          };
        }
      } else if (
        cta.action.store === "ROUTER" &&
        cta.action.type === "ROUTER_STORE POP PAGE START"
      ) {
        return {
          target: "STORE_V2",
          name: cta.name,
          action: popPageStartAction({
            isNative: cta.action.payload.isNative,
          }),
          platform: null,
          analytics: {
            eventName: "BUTTON_CLICKED",
            type: "TRACK",
          },
        };
      } else if (
        cta.action.store === "ROUTER" &&
        cta.action.type === "ROUTER_STORE POP PAGE"
      ) {
        return {
          target: "STORE_V2",
          name: cta.name,
          action: popPageAction(),
          platform: null,
          analytics: {
            eventName: "BUTTON_CLICKED",
            type: "TRACK",
          },
        };
      } else if (
        cta.action.store === "ROUTER" &&
        cta.action.type === "ROUTER_STORE REPLACE PAGE"
      ) {
        return {
          target: "STORE_V2",
          name: cta.name,
          action: replacePageAction({
            layout: cta.action?.payload?.layout,
            content: cta.action?.payload?.content,
          }),
          platform: null,
          analytics: {
            eventName: "BUTTON_CLICKED",
            type: "TRACK",
          },
        };
      }
    } catch (e) {}
    return {
      target: "STORE_V2",
      name: cta.name,
      action: cta.action,
      platform: null,
      analytics: null,
    };
  } else if (cta.target === "GAME_MASTER") {
    if (cta.action === "START") {
      return {
        target: "STORE_V2",
        name: cta.name,
        action: {
          store: "REWARD_SCREEN",
          type: "REWARD_SCREEN CHANGE SCREEN",
          payload: {
            screen: "GAME",
          },
        },
        analytics: {
          eventName: "BUTTON_CLICKED",
          type: "TRACK",
        },
      };
    } else if (cta.action === "RETRY") {
      return {
        target: "STORE_V2",
        name: cta.name,
        action: {
          store: "REWARD",
          type: "UNCONSUME REWARD",
          payload: {},
        },
        analytics: {
          eventName: "TRY_AGAIN_CLICKED",
          type: "TRACK",
        },
      };
    }
  } else if (cta.target === "HREF") {
    return {
      target: "STORE_V2",
      name: cta.name,
      action: {
        store: "EXTERNAL",
        type: "EXTERNAL OPEN_WEB_LINK",
        payload: {
          link: cta.data.link,
          ...cta.data
        },
      },
      analytics: {
        eventName: "BUTTON_CLICKED",
        type: "TRACK",
      },
    };
  } else if (cta.target === "NATIVE") {
    if (cta.event.eventName === "OPEN_DEEPLINK") {
      return {
        target: "STORE_V2",
        name: cta.name,
        action: {
          store: "EXTERNAL",
          type: "EXTERNAL OPEN_DEEPLINK",
          payload: {
            ...cta.event.data,
          },
        },
        analytics: {
          eventName: "BUTTON_CLICKED",
          type: "TRACK",
        },
      };
    } else if (cta.event.eventName === "SHARE") {
      return {
        target: "STORE_V2",
        name: cta.name,
        action: {
          store: "EXTERNAL",
          type: "EXTERNAL SHARE",
          payload: {
            ...cta.event.data,
          },
        },
        analytics: {
          eventName: "BUTTON_CLICKED",
          type: "TRACK",
        },
      };
    } else if (cta.event.eventName === "CLOSE") {
      return {
        target: "STORE_V2",
        name: cta.name,
        action: {
          store: "EXTERNAL",
          type: "EXTERNAL CLOSE",
          payload: null,
        },
        analytics: {
          eventName: "BUTTON_CLICKED",
          type: "TRACK",
        },
      };
    }
  }
};

const getPlatformAction = (
  cta: IStoreCtaV2,
  platform?: string
): IStoreAction | IStoreAction[] => {
  if (platform && cta.platform && cta.platform[platform]) {
    return cta.platform[platform].action;
  }
  return cta.action;
};

const resolveActionPayload = (
  state: any,
  action: IStoreAction,
  cta: ICtaV2
): IStoreAction => {
  if (cta && cta.name && !action.id) {
    action.id = cta.name;
  }
  if (action.payloadStateMap && state) {
    try {
      const newPayload = getStateValues<any>(state, action.payloadStateMap);
      return {
        ...action,
        payload: {...action.payload, ...newPayload},
      };
    } catch (e) {
      console.log(e);
      throw new Error("error resolving stateMap for action");
    }
  } else {
    return action;
  }
};
